import React, { useState, useContext, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    IconButton, Badge, Popover, Box, Typography, Chip, Divider,
    FormControl, InputLabel, Select, MenuItem, Alert,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate } from 'react-router-dom';
import NotificationListItem from '../NotificationListItem/NotificationListItem';
import { StateContext } from '../../contexts/StateContext';
import { useNotificationsContext } from '../../contexts/NotificationsContext';
import { useNotificationsList } from '../../hooks/useNotificationsList';

const SEVERITY = { CRITICAL: 'Critical', WARNING: 'Warning', INFO: 'Info' };

const FILTER_ALL = 'all';
const FILTER_OPTIONS = [FILTER_ALL, SEVERITY.CRITICAL, SEVERITY.WARNING, SEVERITY.INFO];

const MAX_ITEMS = 10;
export const NotificationBell = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { notifBadgeCount = 0 } = useContext(StateContext) || {};
    const { stats, callerKey, silencingUnavailable: statsSilencingUnavailable } = useNotificationsContext();
    const [anchorEl, setAnchorEl] = useState(null);
    const [filter, setFilter] = useState(FILTER_ALL);
    const [sourceFilter, setSourceFilter] = useState('');
    useEffect(() => {
        setFilter(FILTER_ALL);
        setSourceFilter('');
        setAnchorEl(null);
    }, [callerKey]);

    const sourceOptions = useMemo(() => {
        const fromStats = Object.keys(stats?.bySource || {});
        return Array.from(new Set([...(sourceFilter ? [sourceFilter] : []), ...fromStats])).sort();
    }, [stats, sourceFilter]);

    const bellParams = useMemo(() => ({
        notificationType: 'alarm',
        source: sourceFilter || undefined,
        pageSize: MAX_ITEMS,
        sort: 'raisedAtDesc',
    }), [sourceFilter]);

    const open = Boolean(anchorEl);

    // Shared polling hook — keeps the bell in sync with the page and the
    // badge count, and re-fetches immediately whenever the active count
    // changes. Polling is paused while the popover is open (user reading).
    const { items: notifications, silencingUnavailable } = useNotificationsList({
        params: bellParams,
        paused: open,
    });

    const badgeCount = notifBadgeCount;

    const filteredItems = filter === FILTER_ALL
        ? notifications
        : notifications.filter((n) => n.severity === filter);

    const handleBellClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleRowClick = (notification) => {
        handleClose();
        navigate(`/yukon-systems/notifications/${notification.siteId}/${notification.notificationId}`);
    };

    const handleViewAll = () => {
        handleClose();
        navigate('/yukon-systems/notifications');
    };

    const getFilterLabel = (opt) => (opt === FILTER_ALL
        ? t('pages.notifications.bell.filters.all')
        : opt);

    return (
        <>
            <IconButton
                color="inherit"
                onClick={handleBellClick}
                data-testid="notification-bell-button"
                size="large"
                sx={{ mr: 2 }}
            >
                <Badge
                    badgeContent={badgeCount}
                    color="error"
                    invisible={badgeCount === 0}
                    data-testid="notification-bell-badge"
                >
                    <NotificationsIcon />
                </Badge>
            </IconButton>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                data-testid="notification-bell-popover"
                slotProps={{
                    paper: {
                        sx: { width: 400, maxHeight: 520, display: 'flex', flexDirection: 'column' },
                    },
                }}
            >
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
                    <Typography variant="h6">{t('pages.notifications.bell.header')}</Typography>
                </Box>

                <Divider />

                {(silencingUnavailable || statsSilencingUnavailable) && (
                    <Alert severity="warning">{t('pages.notifications.silencing.unavailable')}</Alert>
                )}

                {/* Filter chips */}
                <Box sx={{ display: 'flex', gap: 0.5, px: 2, py: 1, flexWrap: 'wrap' }}>
                    {FILTER_OPTIONS.map((opt) => (
                        <Chip
                            key={opt}
                            label={getFilterLabel(opt)}
                            size="small"
                            variant={filter === opt ? 'filled' : 'outlined'}
                            color={filter === opt ? 'primary' : 'default'}
                            onClick={() => setFilter(opt)}
                            data-testid={`filter-chip-${opt}`}
                            aria-label={`Filter ${getFilterLabel(opt)} notifications`}
                            aria-pressed={filter === opt}
                        />
                    ))}
                </Box>

                <Box sx={{ px: 2, pb: 1 }}>
                    <FormControl size="small" fullWidth>
                        <InputLabel id="bell-source-label">{t('pages.notifications.page.sourceLabel')}</InputLabel>
                        <Select
                            labelId="bell-source-label"
                            value={sourceFilter}
                            label={t('pages.notifications.page.sourceLabel')}
                            onChange={(event) => setSourceFilter(event.target.value)}
                            data-testid="bell-source-filter"
                        >
                            <MenuItem value="">{t('pages.notifications.page.allSources')}</MenuItem>
                            {sourceOptions.map((source) => (
                                <MenuItem key={source} value={source}>{source}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Divider />

                {/* Notification list */}
                <Box sx={{ overflowY: 'auto', flex: 1 }}>
                    {filteredItems.length === 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, color: 'text.secondary' }}>
                            <CheckCircleOutlineIcon sx={{ fontSize: 48, mb: 1, color: '#9e9e9e' }} />
                            <Typography variant="body2">{t('pages.notifications.emptyState.title')}</Typography>
                        </Box>
                    ) : (
                        filteredItems.map((n) => (
                            <NotificationListItem
                                key={n.id}
                                data-testid={`bell-row-${n.id}`}
                                notification={n}
                                onClick={() => handleRowClick(n)}
                            />
                        ))
                    )}
                </Box>

                <Divider />

                {/* Footer */}
                <Box
                    sx={{
                        display: 'flex', justifyContent: 'center', py: 1.5,
                        cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' },
                    }}
                    onClick={handleViewAll}
                    data-testid="bell-view-all"
                >
                    <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                        {t('pages.notifications.bell.viewAll')}
                    </Typography>
                </Box>
            </Popover>
        </>
    );
};

export default NotificationBell;
